import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Factory, Wrench, Settings, Truck, Shield, Users } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultFeatures = [
  { icon: "Factory", title: "In-house Manufacturing", description: "Complete control over quality with our state-of-the-art manufacturing facility in Mumbai." },
  { icon: "Wrench", title: "Project Execution", description: "Experienced team handling end-to-end project delivery." },
  { icon: "Settings", title: "Custom Design Flexibility", description: "Tailor-made solutions designed to match your brand aesthetics and space requirements." },
  { icon: "Truck", title: "Turnkey Installation", description: "Seamless installation with minimal disruption to your business operations." },
  { icon: "Shield", title: "Quality Assurance", description: "ISO 9001:2015 certified products with rigorous quality testing and documentation." },
  { icon: "Users", title: "After-Sales Support", description: "Dedicated support team for maintenance, repairs, and future expansion needs." },
];

const defaultContent = {
  sectionTitle: "Why Corporates Choose Us",
  sectionSubtitle: "Why EZEEFIT",
  description: "We don't just supply furniture — we execute complete workspace transformations with precision and reliability.",
  items: defaultFeatures
};

const iconMap = { Factory, Wrench, Settings, Truck, Shield, Users };

export default function WhyChooseUs() {
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/whyChooseUs`);
        if (response.data && response.data.items?.length > 0) {
          setContent({ ...defaultContent, ...response.data });
        }
      } catch (error) {
        console.log("Using default why choose us");
      }
    };
    fetchContent();
  }, []);

  const getIcon = (iconName, index) => {
    return iconMap[iconName] || [Factory, Wrench, Settings, Truck, Shield, Users][index % 6];
  };

  return (
    <section className="py-24 bg-white" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
            {content.sectionSubtitle}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            {content.sectionTitle}
          </h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
            {content.description}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {content.items?.map((feature, index) => {
            const Icon = getIcon(feature.icon, index);
            return (
              <motion.div
                key={feature.title || index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group p-8 border border-[#E2E8F0] card-hover bg-white"
                data-testid={`feature-${index}`}
              >
                <div className="w-16 h-16 bg-[#F8FAFC] group-hover:bg-[#0F172A] flex items-center justify-center mb-6 transition-colors duration-300">
                  <Icon className="w-8 h-8 text-[#0F172A] group-hover:text-[#F97316] transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
